import { useEffect, useState } from "react";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../utils/calenderOptions";
import Input from "../Input/Input";
import SelectInput from "../selectInput/selectInput";
import SingleDatePicker from "../SingleDatePicker/SingleDatePicker";
import TelInput from "../telInput/telInput";

// Storage key for persisting basic info across navigation
const BASIC_INFO_STORAGE_KEY = "guest_basic_info";

const BasicInfo = ({
  continueClickHandler,
  onClose,
  title,
  subtitle,
  onSaveBasicInfo,
  JourneyLayoutComponent,
  BottomStickyBtnLayoutComponent,
  showCprField = false,
  storageKey, // Optional custom storage key for different flows
  initialValues, // Optional initial values from parent (e.g., from Redux)
}) => {
  const translate = useLanguageTranslate();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Get the storage key to use
  const actualStorageKey = storageKey || BASIC_INFO_STORAGE_KEY;

  // Helper to check if object has any non-empty values
  const hasNonEmptyValues = (obj) => {
    if (!obj) return false;
    return Object.values(obj).some(
      (value) => value !== null && value !== undefined && value !== "",
    );
  };

  // Restore from initialValues (Redux) or localStorage on mount
  useEffect(() => {
    if (isInitialized) return;

    // Priority: initialValues from props (Redux) > localStorage
    if (hasNonEmptyValues(initialValues)) {
      setValues(initialValues);
      setIsInitialized(true);
      return;
    }

    try {
      const savedData = localStorage.getItem(actualStorageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (hasNonEmptyValues(parsedData)) {
          setValues(parsedData);
        }
      }
    } catch (error) {
      console.error("Error restoring basic info from storage:", error);
    }
    setIsInitialized(true);
  }, [initialValues, isInitialized, actualStorageKey]);

  // Helper function to save to localStorage
  const saveToStorage = (formValues) => {
    try {
      localStorage.setItem(actualStorageKey, JSON.stringify(formValues));
    } catch (error) {
      console.error("Error saving basic info to storage:", error);
    }
  };

  // Helper function to save to parent (Redux) on every change
  const saveToParent = (formValues) => {
    if (onSaveBasicInfo) {
      onSaveBasicInfo({
        fullName: formValues.fullName || "",
        email: formValues.email || "",
        contactNumber: formValues.mobile || "",
        birthDate:
          formValues.year && formValues.month && formValues.day
            ? `${formValues.year}-${formValues.month?.padStart(2, "0")}-${formValues.day?.padStart(2, "0")}`
            : "",
      });
    }
  };

  // Custom validation function with specific error messages
  const validateForm = (data) => {
    const formData = data || values;
    const newErrors = {};

    // CPR validation (if field is shown)
    if (showCprField) {
      if (!formData.cpr?.trim()) {
        newErrors.cpr = translate(
          "This field is required.",
          "هذا الحقل مطلوب.",
        );
      } else if (!/^\d{9}$/.test(formData.cpr.trim())) {
        newErrors.cpr = translate(
          "CPR must be 9 digits.",
          "يجب أن يتكون الرقم الشخصي من 9 أرقام.",
        );
      }
    }

    // Full Name validation
    if (!formData.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (!/^[a-zA-Z\s\.]+$/.test(formData.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط.",
      );
    } else if (formData.fullName.trim().split(/\s+/).length < 2) {
      newErrors.fullName = translate(
        "Please enter your full name (first and last name).",
        "يرجى إدخال الاسم الكامل (الاسم الأول والأخير).",
      );
    }
    // Email validation - required first, then format
    if (!formData.email?.trim()) {
      newErrors.email = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح.",
      );
    }

    // Mobile validation - required first, then length
    if (!formData.mobile?.trim()) {
      newErrors.mobile = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (formData.mobile.length !== 8) {
      newErrors.mobile = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح.",
      );
    }

    // Date of birth validation - check if all fields are provided
    if (!formData.day || !formData.month || !formData.year) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else {
      // Check if date is in the future
      const selectedDate = new Date(
        parseInt(formData.year, 10),
        parseInt(formData.month, 10) - 1,
        parseInt(formData.day, 10),
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.dateOfBirth = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل.",
        );
      } else {
        // Check if user is at least 18 years old
        const age = today.getFullYear() - selectedDate.getFullYear();
        const monthDifference = today.getMonth() - selectedDate.getMonth();

        // Adjust age if birthday hasn't occurred this year yet
        const isBeforeBirthday =
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < selectedDate.getDate());

        const actualAge = isBeforeBirthday ? age - 1 : age;

        if (actualAge < 18) {
          newErrors.dateOfBirth = translate(
            "You must be at least 18 years old.",
            "يجب أن تكون 18 عامًا على الأقل.",
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (onSaveBasicInfo) {
        const savedData = {
          fullName: values.fullName,
          email: values.email,
          contactNumber: values.mobile,
          birthDate: `${values.year}-${values.month?.padStart(
            2,
            "0",
          )}-${values.day?.padStart(2, "0")}`,
        };
        if (showCprField) {
          savedData.cpr = values.cpr;
        }
        onSaveBasicInfo(savedData);
      }
      continueClickHandler();
    }
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    const update = {
      ...values,
      day: day,
      month: month,
      year: year,
      fullDate: dateString,
    };

    setValues(update);
    // Only save after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToStorage(update);
      saveToParent(update);
    }

    // Always validate when date changes to show/clear age error immediately
    validateForm(update);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cpr") {
      const numericOnly = value.replace(/[^0-9]/g, "");
      if (numericOnly !== value) return;
    }
    let update = { ...values, [name]: value };
    setValues(update);
    // Only save after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToStorage(update);
      saveToParent(update);
    }
    // Always validate when date fields change to show/clear age error immediately
    if (name === "day" || name === "month" || name === "year") {
      validateForm(update);
    } else if (errors?.[name]) {
      // For other fields, only validate if there's already an error
      validateForm(update);
    }
  };

  return (
    <div className="basic-info-page w-full pb-[120px]">
      <JourneyLayoutComponent
        show_close_btn={true}
        is_hide_back_btn={true}
        isFirstPage={true}
        onClose={onClose}
      >
        <div className="content w-[500px] max767:w-full max767:px-4 ipad:w-[90%] mx-auto">
          <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] text-center max767:text-left max767:rtl:text-right">
            {title}
          </h1>
          <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] text-center mt-1 mb-6 max767:text-left max767:rtl:text-right">
            {subtitle}
          </p>
        </div>
        <form onSubmit={formSubmitHandler} className="basic-info-form w-full">
          <BottomStickyBtnLayoutComponent
            show_summary={false}
            hide_back_btn={true}
            btn_label={translate("Continue", "التالي")}
            btn_onClick={formSubmitHandler}
          >
            <div className="w-[500px] max767:w-full mx-auto ipad:w-[90%] !space-y-6 max767:px-4">
              {showCprField && (
                <Input
                  label={translate("CPR Number", "الرقم الشخصي")}
                  type="text"
                  name="cpr"
                  value={values.cpr || ""}
                  error={errors.cpr}
                  handleChange={handleChange}
                  maxLength={9}
                />
              )}
              <Input
                label={translate("Full Name", "الاسم الكامل")}
                type="text"
                name="fullName"
                value={values.fullName || ""}
                error={errors.fullName}
                handleChange={handleChange}
              />
              <Input
                label={translate("Email Address", "البريد الإلكتروني")}
                type="email"
                name="email"
                value={values.email || ""}
                error={errors.email}
                handleChange={handleChange}
              />
              <TelInput
                name="mobile"
                value={values.mobile || ""}
                error={errors.mobile}
                onChange={handleChange}
                label={translate("Mobile Number", "رقم الهاتف المحمول")}
                className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              />
              <div className="date_of_birth !mt-6">
                <label className="block mb-1 text-[#48535B] not-italic font-normal leading-[26px] text-base tracking-[-0.16px] max-350:text-[13px]">
                  {translate("Date of Birth", "تاريخ الميلاد")}
                </label>

                {/* Mobile: native date picker (visible <=767px) */}
                <div className="hidden max767:block">
                  <SingleDatePicker
                    value={values.fullDate || ""}
                    onChange={handleMobileDatePickerSelect}
                    placeholder={translate("Select Date", "اختر التاريخ")}
                    error={errors.dateOfBirth}
                    useNativeMobile={true}
                  />
                </div>
                {/* Desktop: select dropdowns (visible >767px) */}
                <div className="block max767:hidden">
                  <div className="flex gap-6 self-stretch rtl:flex-row-reverse">
                    <SelectInput
                      name="day"
                      options_data={daysOptions}
                      formData={values}
                      default_placeholder={translate("Day", "يوم")}
                      handleChange={handleChange}
                    />

                    <SelectInput
                      name="month"
                      formData={values}
                      default_placeholder={translate("Month", "شهر")}
                      options_data={monthsOptions}
                      handleChange={handleChange}
                    />

                    <SelectInput
                      name="year"
                      formData={values}
                      default_placeholder={translate("Year", "سنة")}
                      options_data={yearsOptions}
                      handleChange={handleChange}
                    />
                  </div>
                  {errors.dateOfBirth && (
                    <div className="mt-2 text-red-600 text-sm">
                      {errors.dateOfBirth}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </BottomStickyBtnLayoutComponent>
        </form>
      </JourneyLayoutComponent>
    </div>
  );
};

export default BasicInfo;
